// @flow

import * as React from 'react';

import classNames from '@ta/common.classnames';
import { MemberEventOnObjectBlock } from '@ta/social.member-event';

import POICarousel from './POICarousel';

import SponsoredCrust from './SponsoredCrust';
import type { Sponsorship } from './SponsoredCrust';

import styles from './CardSection.less';

type Props = {
  isSingleObject: boolean,
  classNames: string,
  // eslint-disable-next-line no-use-before-define
  topNote?: React.Element<typeof CardSection.Content>,
  header: React.Element<typeof MemberEventOnObjectBlock> | React.Node,
  // eslint-disable-next-line no-use-before-define
  content: React.Element<typeof CardSection.Content> | React.Element<typeof CardSection.Content>[],
  pois: any[],
  socialStatisticsBar?: React.Node,
  socialActionBar?: React.Node,
  // Apply the following class to *ALL* content components. This includes any that are generated within this class (like the carousel)
  contentClass: string,
  /** The clusterid of the section if it has one */
  clusterId?: string,
  sponsorship?: Sponsorship,
};

type ContentProps = {
  children: React.Node,
  /** If a card section is full width, then the contents will take up the entire width of the card. */
  fullWidth: boolean,
};

const CardSection = ({
  topNote,
  header,
  content,
  socialStatisticsBar,
  socialActionBar,
  isSingleObject,
  pois,
  contentClass,
  clusterId,
  sponsorship,
}: Props) => {
  const uiCardStyling = clusterId ? `${styles.cluster_card} section` : 'ui_card section';
  const actualPois = Array.isArray(pois) && pois[0] ? pois : [];
  return (
    <div
      className={classNames(
        styles.card_section,
        isSingleObject ? uiCardStyling : styles.non_section,
      )}
    >
      <React.Fragment>
        {sponsorship && (
          <div className={styles.sponsorship_wrap}>
            <SponsoredCrust sponsorship={sponsorship} />
          </div>
        )}
        {topNote && <div className={styles.top_note}>{topNote}</div>}
        <div className={styles.padding}>{header}</div>
        <div className={classNames(styles.background, contentClass)}>
          {content}
          {actualPois.length ? (
            <CardSection.Content fullWidth>
              <POICarousel pois={actualPois} className={styles.carousel} />
            </CardSection.Content>
          ) : null}
        </div>
        {socialStatisticsBar && <div className={styles.padding}>{socialStatisticsBar}</div>}
        {socialActionBar && <div className={styles.padding}>{socialActionBar}</div>}
      </React.Fragment>
    </div>
  );
};

CardSection.Content = ({ children, fullWidth }: ContentProps) => (
  <React.Fragment>
    {React.Children.map(children, child => (
      <div className={classNames(styles.content, { [styles.fullWidth]: fullWidth })}>{child}</div>
    ))}
  </React.Fragment>
);
CardSection.Content.displayName = 'CardSection.Content';
CardSection.Content.defaultProps = { fullWidth: false };

CardSection.defaultProps = {
  topNote: undefined,
  isSingleObject: false,
  fullWidth: false,
  classNames: '',
  pois: [],
  contentClass: '',
  clusterId: undefined,
};

export default CardSection;
